import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { useAuth, postAuthDestination } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import { startGoogleOAuth } from "@/lib/googleAuth";

export default function Login() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const nextPath = params.get("next");
  const { login, user } = useAuth();
  const [form, setForm] = useState({ email: "", password: "" });
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    if (user) navigate(nextPath || postAuthDestination(user));
  }, [user, navigate, nextPath]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setErrorMsg("");
    try {
      const u = await login(form.email, form.password);
      toast.success("Welcome back!");
      navigate(nextPath || postAuthDestination(u));
    } catch (e) {
      const msg = e?.response?.data?.detail || "Invalid email or password. Please try again.";
      setErrorMsg(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    startGoogleOAuth({ role: "client", next: nextPath || undefined });
  };

  return (
    <div className="min-h-screen bg-kb-bg flex flex-col" data-testid="login-page">
      <Navbar />
      <div className="flex-1 flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-md">
          <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted mb-3">SIGN IN</div>
          <h1 className="font-serif text-4xl text-kb-ink leading-tight">Welcome back.</h1>
          <p className="text-sm text-kb-ink-muted mt-2">Continue your research journey.</p>

          <button
            onClick={googleLogin}
            className="mt-8 w-full border border-slate-300 bg-white text-kb-ink rounded-sm px-4 py-3 flex items-center justify-center gap-3 font-medium hover:border-kb-primary transition-colors"
            data-testid="login-google-btn"
          >
            <svg className="w-4 h-4" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5h-1.8V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.7 1.1 7.8 2.9l5.7-5.7C33.6 5.9 29 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.3-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8c1.8-4.4 6-7.5 11.1-7.5 3 0 5.7 1.1 7.8 2.9l5.7-5.7C33.6 5.9 29 4 24 4 16.3 4 9.6 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5 0 9.5-1.9 13-5l-6-5.1c-1.9 1.4-4.4 2.3-7 2.3-5.3 0-9.7-3.4-11.3-8.1l-6.6 5.1C9.4 39.6 16.1 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.7 2.1-2.1 3.8-3.9 5l6 5.1c-.4.4 6.6-4.8 6.6-14.1 0-1.2-.1-2.3-.4-3.5z"/></svg>
            Continue with Google
          </button>

          <div className="my-6 flex items-center gap-3">
            <div className="flex-1 h-px bg-slate-200" />
            <span className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-light">OR</span>
            <div className="flex-1 h-px bg-slate-200" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            {errorMsg && (
              <div className="bg-rose-50 border border-rose-200 rounded-sm px-3 py-2.5 text-sm text-rose-700 flex items-start gap-2" data-testid="login-error">
                <span className="w-1.5 h-1.5 bg-rose-500 rounded-full mt-1.5 shrink-0" />
                <span>{errorMsg}</span>
              </div>
            )}
            <div>
              <label className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">Email</label>
              <input
                type="email"
                name="email"
                required
                className="kb-input mt-1.5"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="login-email-input"
              />
            </div>
            <div>
              <div className="flex items-baseline justify-between">
                <label className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">Password</label>
                <Link to="/forgot-password" className="kb-mono text-[10px] uppercase tracking-wider text-kb-purple hover:underline" data-testid="login-forgot-link">
                  Forgot password?
                </Link>
              </div>
              <input
                type="password"
                name="password"
                required
                className="kb-input mt-1.5"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                data-testid="login-password-input"
              />
            </div>
            <button type="submit" disabled={submitting} className="kb-btn-primary w-full justify-center" data-testid="login-submit-btn">
              {submitting ? "Signing in…" : (<>Sign in <ArrowRight className="w-4 h-4" /></>)}
            </button>
          </form>

          <div className="mt-8 text-sm text-kb-ink-muted text-center">
            New here?{" "}
            <Link to="/register" className="text-kb-primary font-medium" data-testid="login-register-link">
              Create an account
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
